import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
    deploymentService,
    type UpdateDeploymentDto,
    type DeploymentStatus,
    type DomainHealthResult,
} from '@/services/deployment/deployment.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, Save, Trash2, Rocket, CheckCircle2, XCircle, Loader2, ServerCog } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MetricsDashboard } from '@/components/analytics/metrics-dashboard';
import { UptimePanel } from '@/components/analytics/uptime-panel';
import { ProvisioningPanel } from '@/components/provisioning/provisioning-panel';
import { SshCredentialCard } from '@/components/provisioning/ssh-credential-card';
import { toast } from 'sonner';

const STATUS_LABELS: Record<DeploymentStatus, string> = {
    PENDING_DEPLOYMENT: 'Pending deployment',
    PROVISIONING: 'Provisioning',
    PROVISION_FAILED: 'Provision failed',
    DEPLOYED: 'Deployed',
    SUSPENDED: 'Suspended',
    TERMINATED: 'Terminated',
};

const STATUS_BADGE: Record<DeploymentStatus, string> = {
    PENDING_DEPLOYMENT:
        'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    PROVISIONING:
        'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    PROVISION_FAILED:
        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    DEPLOYED:
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    SUSPENDED:
        'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    TERMINATED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function DeploymentDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState<UpdateDeploymentDto>({
        ipAddress: '',
        frontendDomain: '',
        backendDomain: '',
        filesDomain: '',
    });

    const [activeTab, setActiveTab] = useState('configuration');
    const [deployOpen, setDeployOpen] = useState(false);
    const [creds, setCreds] = useState({ defaultUsername: '', defaultPassword: '' });
    const [healthReport, setHealthReport] = useState<DomainHealthResult[] | null>(null);

    const { data: deployment, isLoading, error } = useQuery({
        queryKey: ['deployment', id],
        queryFn: () => deploymentService.getById(id!),
        enabled: !!id,
    });

    useEffect(() => {
        if (deployment) {
            setFormData({
                ipAddress: deployment.ipAddress,
                frontendDomain: deployment.frontendDomain,
                backendDomain: deployment.backendDomain,
                filesDomain: deployment.filesDomain || '',
                status:
                    deployment.status === 'DEPLOYED' ||
                        deployment.status === 'PROVISIONING'
                        ? undefined
                        : (deployment.status as UpdateDeploymentDto['status']),
            });
        }
    }, [deployment]);

    // Land on the live run rather than making the operator hunt for it.
    const provisioningRef = useRef(false);
    useEffect(() => {
        if (deployment?.status === 'PROVISIONING' && !provisioningRef.current) {
            provisioningRef.current = true;
            setActiveTab('provisioning');
        }
    }, [deployment?.status]);

    const updateMutation = useMutation({
        mutationFn: (data: UpdateDeploymentDto) => deploymentService.update(id!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deployments'] });
            queryClient.invalidateQueries({ queryKey: ['deployment', id] });
            toast.success('Deployment updated successfully');
        },
        onError: (err: unknown) => {
            toast.error(extractMessage(err, 'Failed to update deployment'));
        },
    });

    const deployMutation = useMutation({
        mutationFn: () => deploymentService.deploy(id!, creds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deployments'] });
            queryClient.invalidateQueries({ queryKey: ['deployment', id] });
            setDeployOpen(false);
            setHealthReport(null);
            toast.success('Deployment is live — credentials sent to the customer');
        },
        onError: (err: unknown) => {
            const report = extractHealthReport(err);
            if (report) {
                setHealthReport(report);
            }
            toast.error(extractMessage(err, 'Deployment failed health checks'));
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => deploymentService.delete(id!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deployments'] });
            toast.success('Deployment deleted successfully');
            navigate('/admin/deployments');
        },
        onError: (err: unknown) => {
            toast.error(extractMessage(err, 'Failed to delete deployment'));
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-[400px] w-full" />
            </div>
        );
    }

    if (error || !deployment) {
        return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        Failed to load deployment. Please try again later.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    const isDeployed = deployment.status === 'DEPLOYED';
    const isProvisioning = deployment.status === 'PROVISIONING';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate(-1)} size="sm">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-semibold">Deployment</h1>
                            <Badge className={STATUS_BADGE[deployment.status]}>
                                {STATUS_LABELS[deployment.status]}
                            </Badge>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {deployment.subscription?.company?.name} - {deployment.subscription?.user?.email}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {deployment.status === 'PROVISIONING' && (
                        <Button disabled>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Provisioning...
                        </Button>
                    )}
                    {(deployment.status === 'PENDING_DEPLOYMENT' ||
                        deployment.status === 'PROVISION_FAILED') && (
                            <Button onClick={() => setActiveTab('provisioning')}>
                                <ServerCog className="mr-2 h-4 w-4" />
                                {deployment.status === 'PROVISION_FAILED'
                                    ? 'Retry provisioning'
                                    : 'Provision'}
                            </Button>
                        )}
                    {/* Kept for hosts provisioned out of band, and for a run whose
                        verification timed out but whose host came up afterwards. */}
                    {(deployment.status === 'PENDING_DEPLOYMENT' ||
                        deployment.status === 'PROVISION_FAILED' ||
                        deployment.status === 'SUSPENDED') && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setHealthReport(null);
                                    setDeployOpen(true);
                                }}
                            >
                                <Rocket className="mr-2 h-4 w-4" />
                                Mark live manually
                            </Button>
                        )}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="destructive"
                                size="sm"
                                disabled={deployment.status === 'PROVISIONING'}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete Deployment</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to delete this deployment? This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={() => deleteMutation.mutate()}
                                    className="bg-red-600 hover:bg-red-700"
                                >
                                    Delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList>
                    <TabsTrigger value="configuration">Configuration</TabsTrigger>
                    <TabsTrigger value="provisioning">Provisioning</TabsTrigger>
                    <TabsTrigger value="metrics">Metrics</TabsTrigger>
                    <TabsTrigger value="uptime">Uptime</TabsTrigger>
                </TabsList>

                {/* Always available — this is the provisioning history, not a
                    live-only view — so it is not gated by NotDeployedNotice. */}
                <TabsContent value="provisioning" className="mt-6">
                    <ProvisioningPanel deployment={deployment} />
                </TabsContent>

                <TabsContent value="configuration" className="mt-6 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Deployment Configuration</CardTitle>
                            <CardDescription>
                                Update the deployment settings for this subscription
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    {isDeployed ? (
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            This deployment is live. Suspend or terminate it from the
                                            deployments list if needed.
                                        </p>
                                    ) : isProvisioning ? (
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            A provisioning run is in progress. Cancel it on the
                                            Provisioning tab to change this.
                                        </p>
                                    ) : (
                                        <Select
                                            value={formData.status}
                                            onValueChange={(value: string) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    status: value as UpdateDeploymentDto['status'],
                                                }))
                                            }
                                        >
                                            <SelectTrigger className="w-56">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="PENDING_DEPLOYMENT">Pending deployment</SelectItem>
                                                <SelectItem value="PROVISION_FAILED">Provision failed</SelectItem>
                                                <SelectItem value="SUSPENDED">Suspended</SelectItem>
                                                <SelectItem value="TERMINATED">Terminated</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="ipAddress">IP Address *</Label>
                                        <Input
                                            id="ipAddress"
                                            placeholder="192.168.1.1"
                                            value={formData.ipAddress}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, ipAddress: e.target.value }))
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="frontendDomain">Frontend Domain *</Label>
                                        <Input
                                            id="frontendDomain"
                                            placeholder="app.example.com"
                                            value={formData.frontendDomain}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, frontendDomain: e.target.value }))
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="backendDomain">Backend Domain *</Label>
                                        <Input
                                            id="backendDomain"
                                            placeholder="api.example.com"
                                            value={formData.backendDomain}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, backendDomain: e.target.value }))
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="filesDomain">Files Domain (Optional)</Label>
                                        <Input
                                            id="filesDomain"
                                            placeholder="files.example.com"
                                            value={formData.filesDomain || ''}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, filesDomain: e.target.value }))
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate('/admin/deployments')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={updateMutation.isPending}>
                                        <Save className="mr-2 h-4 w-4" />
                                        {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Subscription</CardTitle>
                        </CardHeader>
                        <CardContent className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                            <Detail label="Company" value={deployment.subscription?.company?.name} />
                            <Detail label="Customer" value={deployment.subscription?.user?.email} />
                            <Detail label="Plan" value={deployment.subscription?.plan?.displayName} />
                            <Detail
                                label="Default username"
                                value={deployment.defaultUsername || '—'}
                            />
                            <Detail
                                label="Deployed at"
                                value={
                                    deployment.deployedAt
                                        ? new Date(deployment.deployedAt).toLocaleString()
                                        : '—'
                                }
                            />
                        </CardContent>
                    </Card>

                    <SshCredentialCard deploymentId={deployment.id} />
                </TabsContent>

                <TabsContent value="metrics" className="mt-6">
                    {isDeployed ? (
                        <MetricsDashboard subscriptionId={deployment.subscriptionId} />
                    ) : (
                        <NotDeployedNotice />
                    )}
                </TabsContent>

                <TabsContent value="uptime" className="mt-6">
                    {isDeployed ? (
                        <UptimePanel subscriptionId={deployment.subscriptionId} />
                    ) : (
                        <NotDeployedNotice />
                    )}
                </TabsContent>
            </Tabs>

            <Dialog open={deployOpen} onOpenChange={setDeployOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deploy this instance</DialogTitle>
                        <DialogDescription>
                            We'll run health checks against the configured domains. On success the
                            deployment is marked live and these credentials are emailed to the customer.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="defaultUsername">Default username</Label>
                            <Input
                                id="defaultUsername"
                                placeholder="admin@example.com"
                                value={creds.defaultUsername}
                                onChange={(e) =>
                                    setCreds((prev) => ({ ...prev, defaultUsername: e.target.value }))
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="defaultPassword">Default password</Label>
                            <Input
                                id="defaultPassword"
                                value={creds.defaultPassword}
                                onChange={(e) =>
                                    setCreds((prev) => ({ ...prev, defaultPassword: e.target.value }))
                                }
                            />
                        </div>

                        {healthReport && (
                            <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 space-y-2">
                                <p className="text-sm font-medium">Health check results</p>
                                {healthReport.map((r) => (
                                    <div key={r.domain} className="flex items-center gap-2 text-sm">
                                        {r.ok ? (
                                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-600" />
                                        )}
                                        <span className="font-mono">{r.domain}</span>
                                        <span className="text-slate-500 dark:text-slate-400">
                                            {r.ok ? `OK${r.status ? ` (${r.status})` : ''}` : r.error}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeployOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => deployMutation.mutate()}
                            disabled={
                                deployMutation.isPending ||
                                !creds.defaultUsername ||
                                !creds.defaultPassword
                            }
                        >
                            <Rocket className="mr-2 h-4 w-4" />
                            {deployMutation.isPending ? 'Deploying...' : 'Run checks & deploy'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
    return (
        <div className="flex flex-col">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="font-medium">{value || '—'}</span>
        </div>
    );
}

function NotDeployedNotice() {
    return (
        <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
                Metrics and uptime become available once this deployment is live.
            </AlertDescription>
        </Alert>
    );
}

function extractMessage(err: unknown, fallback: string): string {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data as { message?: string | string[] } | undefined;
        if (data?.message) {
            return Array.isArray(data.message) ? data.message.join(', ') : data.message;
        }
    }
    if (err instanceof Error) return err.message;
    return fallback;
}

function extractHealthReport(err: unknown): DomainHealthResult[] | null {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data as { healthReport?: DomainHealthResult[] } | undefined;
        if (Array.isArray(data?.healthReport)) return data!.healthReport;
    }
    return null;
}
